import React, { useRef, useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { makeStyles, lighten, alpha } from "@material-ui/core/styles";
import type { Theme } from "src/theme";
import { getContrastText } from "src/utils/colorUtils";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";

import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import { formatCurrencyValue } from "src/utils/formatter";
import { Columns } from "src/pages/TransactionDetails/All";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { createTableToolbar } from "src/components/TableToolbar";
import { calendarYmdInTimeZone, formatZonedDate } from "src/utils/timezone";
import type { MappedTransaction } from "src/interfaces/transaction.interface";

import type { BalanceSheetReport as BalanceSheetReportType } from "./report";
import Title, { DownloadButton } from "./Title";
import CompanyInfo from "./CompanyInfo";
import { render as pdfRender } from "./pdf";
import Details from "./Details";
import { useOpenDialog } from "./useOpenDialog";
import StaleReportWarning from "./StaleReportWarning";

export interface BalanceSheetReportProps {
  report: BalanceSheetReportType;
  showStaleReportWarning?: boolean;
  onRerunReport?: () => void;
  onReportDataMutated?: () => void;
}

interface BalanceSheetTableSectionEntry {
  id: string;
  name?: string;
  amount?: number;
}
interface BalanceSheetTableSection {
  id: string;
  label?: string;
  children: BalanceSheetTableSectionEntry[];
  total?: number;
}
interface BalanceSheetTableProps {
  disableHead?: boolean;
  title: string;
  sections?: BalanceSheetTableSection[];
  total: number;
  padding?: number[];
  onClick?: (arg: {
    section: BalanceSheetTableSection;
    entry?: BalanceSheetTableSectionEntry;
  }) => void;
  selected?: (string | undefined)[];
}

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === "dark";
  const { custom: palette } = theme.palette.custom;
  const headTextColor = getContrastText(palette.accent);
  const defaultTextColor = isDark
    ? theme.palette.common.white
    : theme.palette.common.black;

  return {
    headRow: {
      "& > th, & > td": {
        color: headTextColor,
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: palette.accent,
      },
    },
    totalRow: {
      backgroundColor: isDark
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.08)",
    },
    tableCellHead: {
      lineHeight: 1.43,
      fontWeight: theme.typography.fontWeightBold,
      color: defaultTextColor,
    },
    tableCell: {
      color: defaultTextColor,
    },
    spacer: {
      borderColor: "transparent",
    },
    green: {
      color: isDark
        ? lighten(theme.palette.success.main, 0.2)
        : theme.palette.success.main,
    },
    hover: {
      cursor: "pointer",
      "&:hover": {
        background: theme.palette.action.hover,
      },
    },
    selected: {
      background: alpha(palette.accent, 0.15),
      "&:hover": {
        background: alpha(palette.accent, 0.25),
      },
      "& td:first-child": {
        borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
      },
      "& td:last-child": {
        borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
      },
    },
  };
});

function BalanceSheetTable({
  disableHead,
  title,
  sections,
  total,
  padding,
  onClick,
  selected,
}: BalanceSheetTableProps) {
  const classes = useStyles();

  return (
    <Table size="small">
      {disableHead ? null : (
        <TableHead>
          <TableRow className={classes.headRow}>
            <TableCell className={classes.tableCellHead}>{title}</TableCell>
            <TableCell className={classes.tableCellHead}>Amount</TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {sections
          ? sections.map((s, i) => (
              <React.Fragment key={s.id}>
                <TableRow
                  onClick={onClick ? () => onClick({ section: s }) : undefined}
                >
                  {s.label ? (
                    <TableCell
                      variant="head"
                      colSpan={2}
                      className={clsx(
                        classes.tableCellHead,
                        selected &&
                          selected[0] === s.id &&
                          (selected.length === 1 ||
                            selected[1] === undefined) &&
                          classes.selected
                      )}
                    >
                      {s.label}
                    </TableCell>
                  ) : null}
                </TableRow>
                {s.children.map((c) => (
                  <Tooltip key={c.id} title="Click to view details">
                    <TableRow
                      key={c.id}
                      onClick={
                        onClick
                          ? () => onClick({ section: s, entry: c })
                          : undefined
                      }
                      className={clsx(
                        classes.hover,
                        selected &&
                          s.id === selected[0] &&
                          c.id === selected[1] &&
                          classes.selected
                      )}
                    >
                      {c.name ? (
                        <>
                          <TableCell className={classes.tableCell}>
                            {c.name}
                          </TableCell>
                          <TableCell
                            align="right"
                            className={clsx(
                              classes.tableCell,
                              (c.amount || 0) < 0 && classes.green
                            )}
                          >
                            {formatCurrencyValue(Math.abs(c.amount || 0))}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={2} className={classes.spacer}>
                          ‎
                        </TableCell>
                      )}
                    </TableRow>
                  </Tooltip>
                ))}
                {Array(padding?.[i] || 0)
                  .fill(0)
                  .map((_, i) => (
                    <TableRow key={`padding-${i}`}>
                      <TableCell colSpan={2} className={classes.spacer}>
                        ‎
                      </TableCell>
                    </TableRow>
                  ))}
                {"total" in s && s.total !== undefined ? (
                  <TableRow className={classes.totalRow}>
                    <TableCell className={classes.tableCell}>
                      Total {s.label}
                    </TableCell>
                    <TableCell
                      align="right"
                      className={clsx(
                        classes.tableCell,
                        s.total < 0 && classes.green
                      )}
                    >
                      {formatCurrencyValue(Math.abs(s.total))}
                    </TableCell>
                  </TableRow>
                ) : null}
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className={i + 1 === sections.length ? classes.spacer : ""}
                  >
                    ‎
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          : null}
        {Array(padding?.[sections?.length || 0] || 0)
          .fill(0)
          .map((_, i) => (
            <TableRow key={`padding-${i}`}>
              <TableCell colSpan={2} className={classes.spacer}>
                ‎
              </TableCell>
            </TableRow>
          ))}
        <TableRow className={classes.totalRow}>
          <TableCell className={classes.tableCellHead} variant="head">
            Total {title}
          </TableCell>
          <TableCell
            align="right"
            className={clsx(classes.tableCellHead, total < 0 && classes.green)}
            variant="head"
          >
            {formatCurrencyValue(Math.abs(total))}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} className={classes.spacer}>
            ‎
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function BalanceSheetReport({
  report,
  showStaleReportWarning,
  onRerunReport,
  onReportDataMutated,
}: BalanceSheetReportProps) {
  const dispatch = useDispatch();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const openDialog = useOpenDialog();
  const ref = useRef<HTMLElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const assets = report.entries.find(({ id }) => id === "assets");
  const liabilities = report.entries.find(({ id }) => id === "liabilities");
  const equity = report.entries.find(({ id }) => id === "equity");
  const [selectedKeys, setSelectedKeys] = useState<(string | undefined)[]>();

  // Scroll to details when it opens
  useEffect(() => {
    if (selectedKeys && detailsRef.current) {
      const timeout = setTimeout(() => {
        detailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [selectedKeys]);

  const selected = useMemo(() => {
    if (!selectedKeys) return undefined;
    const group = report.entries.find((e) => e.id === selectedKeys[0]);
    const section = group
      ? group.children.find((e) => e.id === selectedKeys[1])
      : undefined;
    const entry = section
      ? section.children.find((e) => e.id === selectedKeys[2])
      : undefined;

    return {
      group,
      section,
      entry,
    };
  }, [report, selectedKeys]);
  const toolbar = useMemo(() => {
    if (!selected) return undefined;
    return createTableToolbar({
      options: {
        fileName: `Balance Sheet - ${calendarYmdInTimeZone(
          report.parameters.date,
          timeZone
        )} - ${[selected.section?.label, selected.entry?.name]
          .filter(Boolean)
          .join(" - ")}`,
      },
    });
  }, [selected, report.parameters.date, timeZone]);

  if (!assets || !liabilities || !equity) return null;

  const assetsPadding = assets.children.map((g, i) =>
    Math.max(
      0,
      (liabilities.children[i]?.children?.length || g.children.length) -
        g.children.length
    )
  );
  const liabilitiesPadding = liabilities.children.map((g, i) =>
    Math.max(
      0,
      (assets.children[i]?.children?.length || g.children.length) -
        g.children.length
    )
  );

  const assetsLength =
    2 + // header + total row
    assets.children.reduce((t, c) => t + 3 + c.children.length, 0) +
    assetsPadding.reduce((t, p) => t + p, 0);
  const liabilitiesEquityLength =
    3 + // header + total row + last pad
    liabilities.children.reduce((t, c) => t + 3 + c.children.length, 0) +
    liabilitiesPadding.reduce((t, p) => t + p, 0) +
    6 + // Equity has a fixed length
    1; // total (liability + equity)

  const retainedEarnings = equity.children.find(
    (g) => g.id === "retained-earnings"
  );
  const ownerContributionsDraws = equity.children.find(
    (g) => g.id === "owner-contributions-draws"
  );
  const equitySections = [
    {
      id: "equity",
      children: [
        {
          id: "retained-earnings",
          name: "Retained Earnings",
          amount: retainedEarnings?.total || 0,
          transactions:
            retainedEarnings?.children?.reduce((c, e) => {
              c.push(...e.transactions);
              return c;
            }, [] as MappedTransaction[]) || [],
        },
        {
          id: "owner-contributions-draws",
          name: "Owner Contributions/Draws",
          amount: ownerContributionsDraws?.total || 0,
          transactions:
            ownerContributionsDraws?.children?.reduce((c, e) => {
              c.push(...e.transactions);
              return c;
            }, [] as MappedTransaction[]) || [],
        },
      ],
    },
  ];
  const selectedRow = selectedKeys ? selectedKeys.slice(-2) : undefined;

  return (
    <Box component={Paper} p={2} mt={2} width="100%" {...({ ref } as any)}>
      {showStaleReportWarning ? (
        <StaleReportWarning onRerunClick={onRerunReport} />
      ) : null}
      <CompanyInfo />
      <Title
        actions={
          <DownloadButton
            onClick={async () => {
              if (!ref?.current) return;
              const filename = `balance-sheet-report-${calendarYmdInTimeZone(
                report.parameters.date,
                timeZone
              )}.pdf`;
              await pdfRender(ref.current, {
                filename,
                fit: true,
              });
            }}
          />
        }
      >
        Balance Sheet as of{" "}
        {formatZonedDate(report.parameters.date, timeZone, {
          dateStyle: "full",
        })}
      </Title>
      <Box display="flex" width="100%">
        <Box flex={1} pr={2}>
          <BalanceSheetTable
            title="Assets"
            sections={assets.children}
            total={assets.total}
            padding={
              assetsLength < liabilitiesEquityLength
                ? [...assetsPadding, liabilitiesEquityLength - assetsLength]
                : assetsPadding
            }
            onClick={({ section, entry }) => {
              setSelectedKeys(["assets", section.id, entry?.id]);
            }}
            selected={selectedRow}
          />
        </Box>
        <Box flex={1}>
          <BalanceSheetTable
            title="Liabilities"
            sections={liabilities.children}
            total={liabilities.total}
            padding={liabilitiesPadding}
            onClick={({ section, entry }) =>
              setSelectedKeys(["liabilities", section.id, entry?.id])
            }
            selected={selectedRow}
          />
          <BalanceSheetTable
            title="Equity"
            sections={equitySections}
            total={equity.total}
            padding={
              assetsLength > liabilitiesEquityLength
                ? [0, assetsLength - liabilitiesEquityLength]
                : undefined
            }
            onClick={({ section, entry }) =>
              setSelectedKeys(["equity", entry?.id])
            }
            selected={selectedRow}
          />
          <BalanceSheetTable
            disableHead
            title="Liabilities & Equity"
            total={liabilities.total + equity.total}
          />
        </Box>
      </Box>
      <div ref={detailsRef}>
        <Details
          open={!!selected}
          onClose={() => setSelectedKeys(undefined)}
          title={
            selected
              ? [selected.section?.label, selected.entry?.name]
                  .filter(Boolean)
                  .join(" - ")
              : undefined
          }
        >
          {selected ? (
            <XGrid
              Component={CSVFormattedXGrid}
              components={{
                Toolbar: toolbar,
              }}
              rows={
                (selected.entry as any)?.transactions ||
                selected.section?.children.reduce((t, e) => {
                  t.push(...(e as any).transactions);
                  return t;
                }, [] as any[])
              }
              columns={Columns}
              headerHeight={35}
              rowHeight={35}
              onCellDoubleClick={async (ev) => {
                if (ev.row?.journal_entry_id) {
                  dispatch(
                    setActiveDialog("add_journal", {
                      id: ev.row.journal_entry_id,
                    })
                  );
                  return;
                }
                if (ev.row.collection === "transaction") {
                  const res = await openDialog(
                    "transaction",
                    ev.row.collection_id
                  );
                  if (res.status === "updated") onReportDataMutated?.();
                  return;
                }

                if (ev.row.collection === "inventory") {
                  const res = await openDialog(
                    "inventory",
                    ev.row.collection_id
                  );
                  if (res.status === "updated") onReportDataMutated?.();
                  return;
                }

                if (ev.row.collection === "sale") {
                  const res = await openDialog("sale", ev.row.collection_id);
                  if (res.status === "updated") onReportDataMutated?.();
                  return;
                }
              }}
            />
          ) : null}
        </Details>
      </div>
    </Box>
  );
}

export default BalanceSheetReport;
